/**
 * MSAL Configuration for Azure Entra ID SSO (AXIS175)
 * Replaces local JWT authentication with Azure Entra ID OAuth2 authorization code flow with PKCE
 */

import { Configuration, PublicClientApplication } from '@azure/msal-browser';

// Environment-specific redirect URIs
const getRedirectUri = (): string => {
  // Use environment variable if available, otherwise use defaults based on hostname
  const envRedirectUri = import.meta.env.VITE_OAUTH_REDIRECT_URI;
  if (envRedirectUri) {
    return envRedirectUri;
  }

  // Default to dev environment
  return 'https://thankful-dune-0cb4f5d00.1.azurestaticapps.net/oauth/callback';
};

// Azure Entra ID configuration
const ENTRA_CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID || 'ade2c4d7-2eb3-420c-8e91-d2ace71647e0';
const ENTRA_TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID || '3f76a5a7-4aef-4e88-82bd-38db9ea45d48';

// MSAL configuration
export const msalConfig: Configuration = {
  auth: {
    clientId: ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${ENTRA_TENANT_ID}`,
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: '/',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage', // Use sessionStorage for PHI application
    storeAuthStateInCookie: false,
  },
};

// Scopes for token acquisition
// ID token scopes for authentication
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// Scopes for API access (used by acquireTokenSilent)
// For Entra ID, we use the client ID as the scope for ID token validation
export const apiRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// API endpoints
export const apiConfig = {
  functionAppUrl: import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net',
};

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL (must be called before using MSAL)
export const initializeMsal = async (): Promise<void> => {
  await msalInstance.initialize();
};

// Get active account
export const getActiveAccount = () => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    return accounts[0];
  }
  return null;
};

// Acquire token silently (gets ID token for our use case)
export const acquireTokenSilent = async () => {
  const account = getActiveAccount();
  if (!account) {
    throw new Error('No active account');
  }

  try {
    // Always use msalInstance.acquireTokenSilent to get a fresh ID token
    // This ensures we get a valid Entra ID token (RS256 algorithm)
    // rather than potentially stale cached tokens
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email'],
      account,
      // Force refresh if token is expired or about to expire
      forceRefresh: false, // Let MSAL handle refresh automatically
    });

    // Return the ID token from the response (not access token)
    // For Entra ID, we use the ID token to validate user identity
    return {
      accessToken: response.idToken,  // Use ID token as bearer token
      idToken: response.idToken,
      account: account,
    };
  } catch (error: any) {
    // If silent token acquisition fails (e.g., refresh token expired),
    // we need to re-authenticate interactively
    if (error.name === 'InteractionRequiredAuthError') {
      console.warn('Silent token acquisition failed, interactive login required');
      // Trigger redirect to login
      await loginRedirect();
      throw new Error('Session expired. Please sign in again.');
    }
    throw error;
  }
};

// Login redirect (PKCE authorization code flow)
export const loginRedirect = async () => {
  await msalInstance.loginRedirect(loginRequest);
};

// Logout
export const logout = async () => {
  await msalInstance.logoutRedirect();
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0;
};

// Get user info from token claims
export const getUserInfo = () => {
  const account = getActiveAccount();
  if (!account) {
    return null;
  }

  return {
    id: account.localAccountId,
    email: account.username,
    name: account.name,
    oid: account.idTokenClaims?.oid,
    roles: account.idTokenClaims?.roles || [],
  };
};
